/** @file Trusted user edits and entry into the existing reassignment workspace. */
import { appData, tempUsersData, setTempUsersData } from "../../app/state/state.js";
import * as callables from "../../app/firebase/callables.js";
import { getNormalizedUserPermissions, COLLECTION_PERMISSION_OPTIONS } from "./permissionModel.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { closeForm } from "../../shared/forms/modalShell.js";

const FIELDS = ["name", "role", "permissions", "managerUserId", "isAppProvider",
  "drugRole", "drugSortOrder", "isDrugAdmin", "isProvider", "siteIds", "defaultSiteId"];
let baselineUsers = new Map();

/**
 * Captures the Settings entry snapshot before any user edits.
 * @param {Array<object>} users Displayed users.
 * @returns {void}
 */
export function captureUserSettingsBaseline(users) {
  baselineUsers = new Map(users.map((user) => [user.id, structuredClone(user)]));
}

/**
 * Compares editable values using the established display normalisation.
 * @param {object} user Source user.
 * @param {string} field Editable field.
 * @returns {*} Comparable value.
 */
function comparable(user, field) {
  if (field === "permissions") {
    return getNormalizedUserPermissions(user.role, user.permissions).sort();
  }
  if (field === "siteIds") {return [...(user.siteIds || [])].sort();}
  if (["isAppProvider", "isDrugAdmin", "isProvider"].includes(field)) {
    return user[field] === true;
  }
  if (field === "drugSortOrder") {
    return user[field] === "" || user[field] === null || user[field] === undefined ? null : Number(user[field]);
  }
  return user[field] ?? null;
}

/**
 * Builds a partial trusted save with expected values from the editing snapshot.
 * @param {object} original Editing snapshot.
 * @param {object} proposed Proposed editable fields.
 * @returns {object|null} Changed values and expected values, or no change.
 */
export function buildUserSettingsEdit(original, proposed) {
  const intended = {...original, ...proposed};
  const changes = {};
  const expected = {};
  FIELDS.filter((field) => Object.hasOwn(proposed, field)).forEach((field) => {
    if (JSON.stringify(comparable(original, field)) ===
        JSON.stringify(comparable(intended, field))) {return;}
    changes[field] = field === "drugSortOrder" ? comparable(intended, field) :
      intended[field];
    expected[field] = original[field] ?? null;
  });
  // Membership/default are one canonical save unit.
  if ("siteIds" in changes || "defaultSiteId" in changes) {
    changes.siteIds = intended.siteIds;
    changes.defaultSiteId = intended.defaultSiteId;
    expected.siteIds = original.siteIds ?? null;
    expected.defaultSiteId = original.defaultSiteId ?? null;
  }
  return Object.keys(changes).length ? {targetUserId: original.id,
    changes, expected} : null;
}

/**
 * Builds user edits from the retained Settings entry snapshot.
 * @param {Array<object>} users Settings draft users.
 * @returns {Array<object>} Partial edits.
 */
export function getUserSettingsEdits(users = tempUsersData || []) {
  return users.flatMap((user) => {
    const original = baselineUsers.get(user.id) ||
      appData.users.find((entry) => entry.id === user.id);
    if (!original || original.isActive === false) {return [];}
    const edit = buildUserSettingsEdit(original, user);
    return edit ? [edit] : [];
  });
}

/**
 * Applies confirmed editable fields without replacing unrelated user metadata.
 * @param {object} user Confirmed callable user.
 * @returns {void}
 */
export function applyConfirmedUserSettings(user) {
  const merge = (source) => source.map((entry) => {
    if (entry.id !== user.id) {return entry;}
    const updated = {...entry, ...user};
    // The confirmed editable snapshot omits deleted optional fields.
    if (!Object.hasOwn(user, "drugSortOrder")) {delete updated.drugSortOrder;}
    return updated;
  });
  appData.users = merge(appData.users || []);
  if (tempUsersData) {setTempUsersData(merge(tempUsersData));}
  baselineUsers.set(user.id, structuredClone(appData.users.find((entry) =>
    entry.id === user.id)));
}

/**
 * Saves one ordinary user edit through backend authority.
 * @param {object} edit Partial edit with expected values.
 * @returns {Promise<object>} Confirmed save.
 */
export async function saveTrustedUserSettings(edit) {
  const result = (await callables.saveUserSettingsCallable(edit)).data;
  if (result?.success !== true) {throw new Error("User save did not complete.");}
  applyConfirmedUserSettings(result.user);
  return result;
}

/**
 * Gets current authoritative impact before any Settings writes.
 * @param {object} edit Partial proposed user edit.
 * @returns {Promise<object>} Current scoped impact.
 */
export async function checkUserSettingsEdit(edit) {
  const impact = (await callables.checkUserAccessChangeImpactCallable(edit)).data;
  if (impact.limitations?.length) {
    throw new Error("This access change is unavailable until its location checks are complete.");
  }
  return impact;
}

/**
 * Describes pending changes using module labels and site display names.
 * @param {object} edit Partial edit.
 * @returns {string} Plain change summary.
 */
export function describeUserAccessEdit(edit) {
  return Object.keys(edit?.changes || {}).map((field) => {
    if (field === "permissions") {
      const before = getNormalizedUserPermissions(edit.expected?.role || "user",
        edit.expected?.permissions || []);
      const after = getNormalizedUserPermissions(edit.changes.role ||
        edit.expected?.role || "user", edit.changes.permissions || []);
      const label = (id) => COLLECTION_PERMISSION_OPTIONS.find((option) =>
        option.value === id)?.label || id;
      const removed = before.filter((id) => !after.includes(id)).map(label);
      const added = after.filter((id) => !before.includes(id)).map(label);
      return [removed.length ? `Remove ${removed.join(", ")}` : "",
        added.length ? `Add ${added.join(", ")}` : ""].filter(Boolean).join("; ") ||
        "Collection permissions";
    }
    if (["siteIds", "defaultSiteId"].includes(field)) {return "Site access";}
    return ({isAppProvider: "App provider", drugRole: "Drug role",
      isDrugAdmin: "Drug administrator", isProvider: "Drug provider",
      role: "Role"})[field] || field;
  }).filter((value, index, values) => values.indexOf(value) === index).join(", ");
}

/**
 * Starts an explicit review and opens the existing Users workspace.
 * @param {object} edit Proposed settings edit.
 * @returns {Promise<void>}
 */
export async function startUserAccessReview(edit) {
  const payload = (await callables.openUserAccessChangeDraftCallable(edit)).data;
  const original = baselineUsers.get(edit.targetUserId) ||
    appData.users.find((user) => user.id === edit.targetUserId);
  if (original && tempUsersData) {
    setTempUsersData(tempUsersData.map((user) => user.id === edit.targetUserId ?
      structuredClone(original) : user));
  }
  closeForm();
  const {upsertDeactivationDraftSummary} = await import("./userDeactivationSettings.js");
  upsertDeactivationDraftSummary(payload);
  const {showUserAccessReviewWorkspace} = await import("./settingsController.js");
  showUserAccessReviewWorkspace(payload);
}

/**
 * Runs all user preflights before config, Compliance, teams or users are saved.
 * @param {Array<object>} [users] Settings draft users.
 * @returns {Promise<boolean>} True when ordinary save can proceed.
 */
export async function preflightUserSettingsChanges(users = tempUsersData || []) {
  try {
    for (const edit of getUserSettingsEdits(users)) {
      const impact = await checkUserSettingsEdit(edit);
      if (!impact.summary.rowCount) {continue;}
      const user = appData.users.find((entry) => entry.id === edit.targetUserId);
      openConfirmationModal("Review access changes",
        `<p>${escapeHTML(user?.name || "This user")}: ${escapeHTML(describeUserAccessEdit(edit))}.</p>` +
        `<p class="mt-2">${impact.summary.rowCount} affected work items need review before these changes can be applied.</p>`,
        () => startUserAccessReview(edit), false, "Review affected work",
        null, "Opening review…", "Back to editing");
      return false;
    }
    return true;
  } catch (error) {
    openConfirmationModal("Access changes not saved", error.message,
      () => {}, false, "OK");
    return false;
  }
}
